import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import { getConfigActive, majVisionConfig, majIntegriteConfig, getIntegriteChangements, majReglagesArgent, getChangementsArgent, getEntitesFacturation, majEntiteFacturation, getDocumentsEmis, telechargerDocumentEmis } from "../utilities/adminservice";
import { getSyntheseIntegrite, getTesteurs, enregistrerTesteur, supprimerTesteur } from "../utilities/locationService";
import { estSuperAdmin } from "../api/role";
import EnTetePage from "../components/charte/EnTetePage";
import TroisBarres from "../components/charte/TroisBarres";
import { Domaine } from "../theme/tribeTheme";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { loaderOff, loaderOn, logout, openSnackbar } from "../redux/actions";
import store from "../redux/store";
import backofficeApi from "../utilities/backofficeApi";
import {
  getActiveAddMore,
  createAddmore,
  updateAddmore,
  deleteAddMore,
  getAddmorebyid,
} from "../utilities/backofficeservice";
import { getUser } from "../utilities/adminservice";
import CommonDialog from "../commondialog/confirm";
import { messageErreur, getTypeLieuCategories } from "../utilities/typeLieuService";
import { getSeuilsFraicheur, reglerSeuilFraicheur, retirerSeuilFraicheur, messageSeuil } from "../utilities/seuilsFraicheurService";
import { AIDE_ARGENT, AIDE_REGLAGES } from "../aide/textesAide";
import BoutonAide from "../components/charte/BoutonAide";

// ─── Design tokens ──────────────────────────────────────────────────────────
/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg: "var(--c-bg)",
  card: "var(--c-surface)",
  border: "var(--c-border)",
  borderAccent: "#1F1E1B",
  accent: "#1F1E1B",
  accentLight: "#57534A",
  accentMuted: "rgba(31,30,27,0.07)",
  text: "#1F1E1B",
  textSub: "#57534A",
  textMuted: "#57534A",
  success: "#356A0F",
  warning: "#7C5600",
  error: "#A23A0D",
} as const;

const inputSx = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(31,30,27,0.04)",
    color: C.text,
    borderRadius: 1.5,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderAccent },
    "&.Mui-focused fieldset": { borderColor: C.accentLight },
  },
  "& .MuiFormHelperText-root": { color: C.error },
  "& input": { color: C.text },
  "& label": { color: C.textMuted },
};

const headCellSx = {
  background: C.card,
  color: C.accentLight,
  fontWeight: 700,
  fontSize: "11px",
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  borderBottom: `1px solid ${C.border}`,
};

const rowSx = {
  "& .MuiTableCell-root": {
    color: C.text,
    borderBottom: `1px solid ${C.border}`,
    py: 1.5,
  },
};

const modalBoxSx = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  width: 600,
  background: C.card,
  border: `1px solid ${C.borderAccent}`,
  borderRadius: 2,
  p: 4,
  outline: "none",
  maxHeight: "90vh",
  overflowY: "auto" as const,
};

const accentBtn = {
  background: C.accent,
  color: "#fff",
  borderRadius: 1.5,
  textTransform: "none" as const,
  fontWeight: 600,
  "&:hover": { background: C.accentLight },
  "&.Mui-disabled": { background: "rgba(31,30,27,0.2)", color: C.textMuted },
};

const accordionSx = {
  background: C.card,
  border: "1.5px solid #1F1E1B",
  borderRadius: "12px !important",
  mb: 2,
  "&:before": { display: "none" },
  boxShadow: "none",
};

const accordionSummarySx = {
  "& .MuiAccordionSummary-content": { margin: "12px 0" },
  borderRadius: "8px",
};

// ════════════════════════════════════════════════════════════════════════════
// SECTION 1 — Valeur du Tip et règles de retrait (décisions D1 et D2)
// ════════════════════════════════════════════════════════════════════════════
// Seule source : la configuration active d'admin-service. Modification par un
// SUPER_ADMIN uniquement, motif obligatoire, chaque changement journalisé.
// Les règles sont appliquées par user-service : l'app n'en décide plus.
const ARGENT_CHAMPS = [
  { key: "tipValueCfa", label: "Valeur d'un Tip (F CFA)" },
  { key: "retraitMinimumCfa", label: "Minimum par retrait (F CFA)" },
  { key: "retraitPlafondMensuelCfa", label: "Plafond mensuel par Spotter (F CFA)" },
  { key: "retraitSeuilDoubleApprobationCfa", label: "Second avis au-delà de (F CFA)" },
];

const LIBELLES_ARGENT: Record<string, string> = {
  tipValueCfa: "Valeur du Tip",
  retraitMinimumCfa: "Minimum par retrait",
  retraitPlafondMensuelCfa: "Plafond mensuel",
  retraitSeuilDoubleApprobationCfa: "Seuil du second avis",
  retraitDoubleApprobationActive: "Second avis activé",
};

function TipsSection() {
  const dispatch = useDispatch();
  const superAdmin = estSuperAdmin();
  const [valeurs, setValeurs] = useState<any>(null);
  const [initiales, setInitiales] = useState<any>(null);
  const [motif, setMotif] = useState("");
  const [historique, setHistorique] = useState<any[]>([]);
  const [confirmer, setConfirmer] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const charger = async () => {
    const c: any = await getConfigActive();
    if (c) {
      const v = {
        tipValueCfa: c.tipValueCfa ?? "",
        retraitMinimumCfa: c.retraitMinimumCfa ?? "",
        retraitPlafondMensuelCfa: c.retraitPlafondMensuelCfa ?? "",
        retraitSeuilDoubleApprobationCfa: c.retraitSeuilDoubleApprobationCfa ?? "",
        retraitDoubleApprobationActive: !!c.retraitDoubleApprobationActive,
      };
      setValeurs(v);
      setInitiales(v);
    }
    setHistorique(await getChangementsArgent());
  };

  useEffect(() => { charger(); }, []);

  if (!valeurs) return <Typography sx={{ fontSize: 13, color: C.textSub }}>Chargement…</Typography>;

  const modifies = Object.keys(valeurs).filter((k) => String(valeurs[k]) !== String(initiales[k]));

  const enregistrer = async () => {
    setConfirmer(false);
    setEnCours(true);
    try {
      await majReglagesArgent({ ...valeurs, motif: motif.trim() });
      dispatch(openSnackbar("Réglages d'argent enregistrés et journalisés", "green"));
      setMotif("");
      await charger();
    } catch (e: any) {
      const msg = typeof e?.response?.data === "string" ? e.response.data : "Enregistrement refusé";
      dispatch(openSnackbar(e?.response?.status === 403 ? "Réservé au super admin" : msg, "red"));
    }
    setEnCours(false);
  };

  const tip = Number(valeurs.tipValueCfa);

  return (
    <Box>
      {!superAdmin && (
        <Typography sx={{ fontSize: 13, color: C.textSub, mb: 2 }}>
          Lecture seule : seul un super admin peut modifier ces réglages.
        </Typography>
      )}
      <Grid container spacing={3}>
        {ARGENT_CHAMPS.map(({ key, label }) => (
          <Grid item lg={3} md={6} xs={12} key={key}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.textSub, mb: 0.75 }}>{label}</Typography>
            <TextField
              type="number"
              fullWidth
              size="small"
              value={valeurs[key]}
              inputProps={{ readOnly: !superAdmin, min: 1 }}
              onChange={(e) => setValeurs({ ...valeurs, [key]: e.target.value })}
              sx={inputSx}
            />
          </Grid>
        ))}
      </Grid>
      <FormControlLabel
        sx={{ mt: 2, color: C.text }}
        control={
          <Switch
            checked={valeurs.retraitDoubleApprobationActive}
            disabled={!superAdmin}
            onChange={(e) => setValeurs({ ...valeurs, retraitDoubleApprobationActive: e.target.checked })}
          />
        }
        label="Second avis d'un autre admin au-delà du seuil (désactivé par défaut)"
      />
      {tip > 0 && (
        <Box sx={{ mt: 2, px: 2, py: 1.5, borderRadius: 1.5, background: C.accentMuted, border: `1px solid ${C.border}` }}>
          <Typography sx={{ fontSize: 13, color: C.accentLight }}>
            <strong>1 000 Tips = {(1000 * tip).toLocaleString("fr-FR")} F CFA</strong>
          </Typography>
        </Box>
      )}
      {superAdmin && (
        <Box sx={{ mt: 3, display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
          <TextField
            size="small"
            label="Motif du changement (obligatoire)"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            sx={{ ...inputSx, minWidth: 320 }}
          />
          <Button
            disabled={enCours || modifies.length === 0 || motif.trim() === ""}
            onClick={() => setConfirmer(true)}
            sx={accentBtn}
          >
            Enregistrer
          </Button>
        </Box>
      )}
      <CommonDialog
        open={confirmer}
        contentText={
          "Changements : " +
          modifies.map((k) => `${LIBELLES_ARGENT[k]} ${initiales[k]} → ${valeurs[k]}`).join(" ; ") +
          ". Ils s'appliquent immédiatement à toutes les conversions et à tous les retraits."
        }
        handleClose={() => setConfirmer(false)}
        handleConfirm={enregistrer}
      />
      {historique.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, mb: 1 }}>Journal des changements</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Date", "Réglage", "Avant", "Après", "Motif", "Auteur"].map((h) => (
                    <TableCell key={h} sx={{ color: C.textSub, fontWeight: 600 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {historique.slice(0, 20).map((h: any) => (
                  <TableRow key={h.id}>
                    <TableCell sx={{ color: C.text }}>{h.dateChangement ? new Date(h.dateChangement).toLocaleString("fr-FR") : ""}</TableCell>
                    <TableCell sx={{ color: C.text }}>{LIBELLES_ARGENT[h.champ] ?? h.champ}</TableCell>
                    <TableCell sx={{ color: C.text }}>{h.ancienneValeur}</TableCell>
                    <TableCell sx={{ color: C.text }}>{h.nouvelleValeur}</TableCell>
                    <TableCell sx={{ color: C.text }}>{h.motif}</TableCell>
                    <TableCell sx={{ color: C.text }}>{h.auteur}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SECTION — Facturation (décision D10)
// ════════════════════════════════════════════════════════════════════════════
// Entité émettrice choisie selon le pays de la mission. Une entité incomplète
// n'émet aucun document. Modification : super admin, motif obligatoire.
// Identifiants légaux selon le pays de l'entité : NINEA + RCCM (Sénégal),
// NCC + RCCM + régime + centre des impôts (Côte d'Ivoire, exigés par la FNE),
// champ libre ailleurs. Préfixe et devise (XOF) : lecture seule une fois posés.
type ChampEntite = { key: string; label: string; pays?: string[]; horsPays?: string[]; multi?: boolean };
const CHAMPS_ENTITE: ChampEntite[] = [
  { key: "raisonSociale", label: "Raison sociale" },
  { key: "formeEtCapital", label: "Forme et capital" },
  { key: "adresse", label: "Adresse du siège" },
  { key: "ninea", label: "NINEA", pays: ["SN"] },
  { key: "ncc", label: "NCC (numéro de compte contribuable)", pays: ["CI"] },
  { key: "rccm", label: "RCCM", pays: ["SN", "CI"] },
  { key: "regimeImposition", label: "Régime d'imposition", pays: ["CI"] },
  { key: "centreImpots", label: "Centre des impôts", pays: ["CI"] },
  { key: "identifiantsLegaux", label: "Identifiants légaux", horsPays: ["SN", "CI"] },
  { key: "representant", label: "Représentant (nom, qualité)" },
  { key: "telephone", label: "Téléphone" },
  { key: "email", label: "Email de contact" },
  { key: "banque", label: "Banque" },
  { key: "rib", label: "RIB" },
  { key: "iban", label: "IBAN" },
  { key: "tauxTva", label: "Taux de TVA (ex. 0.18)" },
  { key: "delaiPaiementJours", label: "Délai de paiement (jours)" },
  { key: "conditionsPaiement", label: "Conditions de paiement (imprimées)", multi: true },
  { key: "penalitesRetard", label: "Pénalités de retard (imprimées)", multi: true },
  { key: "mentions", label: "Mentions de bas de page", multi: true },
];
const champVisible = (c: ChampEntite, pays: string) =>
  (!c.pays || c.pays.includes(pays)) && (!c.horsPays || !c.horsPays.includes(pays));

function FacturationSection() {
  const dispatch = useDispatch();
  const superAdmin = estSuperAdmin();
  const [entites, setEntites] = useState<any[]>([]);
  const [edition, setEdition] = useState<any>(null);
  const [motif, setMotif] = useState("");
  const [documents, setDocuments] = useState<any[]>([]);

  const charger = async () => {
    setEntites(await getEntitesFacturation());
    setDocuments(await getDocumentsEmis());
  };
  useEffect(() => { charger(); }, []);

  const enregistrer = async () => {
    try {
      // La devise n'est plus saisie : XOF, fixée par le serveur.
      const { devise, ...champs } = edition;
      await majEntiteFacturation(edition.pays, { ...champs, motif: motif.trim() });
      dispatch(openSnackbar("Entité enregistrée et journalisée", "green"));
      setEdition(null); setMotif("");
      charger();
    } catch (e: any) {
      const msg = typeof e?.response?.data === "string" ? e.response.data : "Enregistrement refusé";
      dispatch(openSnackbar(e?.response?.status === 403 ? "Réservé au super admin" : msg, "red"));
    }
  };

  return (
    <Box>
      {entites.map(({ entite, complete, manquants }: any) => (
        <Box key={entite.pays} sx={{ mb: 2, p: 2, borderRadius: 1.5, border: `1px solid ${C.border}` }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text }}>
            {entite.pays} — {entite.raisonSociale || "(sans raison sociale)"}{" "}
            <span style={{ color: complete ? "#356A0F" : "#A23A0D", fontWeight: 600 }}>
              {complete ? "· complète" : "· incomplète : aucun document émis"}
            </span>
          </Typography>
          <Typography sx={{ fontSize: 12, color: C.textSub, mt: 0.5 }}>
            {[entite.prefixe && `Préfixe ${entite.prefixe}`, entite.adresse, entite.ninea && `NINEA ${entite.ninea}`,
              entite.ncc && `NCC ${entite.ncc}`, entite.rccm && `RCCM ${entite.rccm}`,
              entite.tauxTva != null ? `TVA ${Math.round(entite.tauxTva * 100)} %` : null, entite.devise,
              entite.certificationFiscaleRequise ? "certification fiscale requise" : null].filter(Boolean).join(" · ")}
          </Typography>
          {!complete && Array.isArray(manquants) && manquants.length > 0 && (
            <Typography sx={{ fontSize: 12, color: "#A23A0D", mt: 0.5 }}>
              À compléter avant toute émission : {manquants.join(", ")}.
            </Typography>
          )}
          {superAdmin && (
            <Button sx={{ ...accentBtn, mt: 1 }} onClick={() => { setEdition({ ...entite }); setMotif(""); }}>
              Modifier
            </Button>
          )}
        </Box>
      ))}
      {edition && (
        <Box sx={{ mb: 3, p: 2, borderRadius: 1.5, border: `1px solid ${C.border}` }}>
          <Grid container spacing={2}>
            <Grid item md={6} xs={12} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField fullWidth size="small" label="Préfixe des numéros" value={edition.prefixe ?? ""} sx={inputSx}
                disabled={!!entites.find((x: any) => x.entite.pays === edition.pays)?.entite?.prefixe}
                onChange={(ev) => setEdition({ ...edition, prefixe: ev.target.value })} />
              <BoutonAide texte={AIDE_ARGENT.entitePrefixe} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField fullWidth size="small" label="Devise des documents" value="XOF" disabled sx={inputSx} />
            </Grid>
            {CHAMPS_ENTITE.filter((c) => champVisible(c, edition.pays)).map(({ key, label, multi }) => (
              <Grid item md={multi ? 12 : 6} xs={12} key={key}>
                <TextField fullWidth size="small" label={label} value={edition[key] ?? ""} sx={inputSx}
                  multiline={multi} minRows={multi ? 2 : undefined}
                  onChange={(ev) => setEdition({ ...edition, [key]: ev.target.value })} />
              </Grid>
            ))}
            <Grid item xs={12} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FormControlLabel label="Certification fiscale requise (factures et avoirs)" control={
                <Checkbox checked={!!edition.certificationFiscaleRequise}
                  onChange={(ev) => setEdition({ ...edition, certificationFiscaleRequise: ev.target.checked })} />} />
              <BoutonAide texte={AIDE_ARGENT.entiteCertification} />
            </Grid>
            <Grid item md={8} xs={12}>
              <TextField fullWidth size="small" label="Motif du changement (obligatoire)" value={motif} sx={inputSx}
                onChange={(ev) => setMotif(ev.target.value)} />
            </Grid>
            <Grid item md={4} xs={12} sx={{ display: "flex", gap: 1 }}>
              <Button sx={accentBtn} disabled={!motif.trim()} onClick={enregistrer}>Enregistrer</Button>
              <Button onClick={() => setEdition(null)}>Annuler</Button>
            </Grid>
          </Grid>
        </Box>
      )}
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, mt: 2, mb: 1 }}>Documents émis (archive)</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {["Numéro", "Type", "Mission", "HT", "TTC", "Émis le", "Par", "PDF"].map((h) => (
                <TableCell key={h} sx={{ color: C.textSub, fontWeight: 600 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((d: any) => (
              <TableRow key={d.id}>
                <TableCell sx={{ color: C.text }}>{d.numero}</TableCell>
                <TableCell sx={{ color: C.text }}>{d.type}</TableCell>
                <TableCell sx={{ color: C.text }}>{d.titreMission}</TableCell>
                <TableCell sx={{ color: C.text }}>{Number(d.montantHt).toLocaleString("fr-FR")}</TableCell>
                <TableCell sx={{ color: C.text }}>{Number(d.montantTtc).toLocaleString("fr-FR")} {d.devise}</TableCell>
                <TableCell sx={{ color: C.text }}>{d.emisLe ? new Date(d.emisLe).toLocaleDateString("fr-FR") : ""}</TableCell>
                <TableCell sx={{ color: C.text }}>{d.emisPar}</TableCell>
                <TableCell>
                  {d.pdfArchive
                    ? <Button size="small" onClick={() => telechargerDocumentEmis(d.id, d.numero)}>Télécharger</Button>
                    : <span style={{ color: C.textSub }}>non archivé</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SECTION 2 — Fraîcheur des données (Z7-30)
// ════════════════════════════════════════════════════════════════════════════
/* Un seul réglage : le nombre de jours au-delà duquel un POI Spotter d'une
   catégorie est « périmé » pour la détection des trous. Les anciens paliers
   couleur/jours n'étaient lus par rien et ont été retirés. */
interface LigneSeuil {
  categorieId: number;
  libelle: string;
  regle: number | null;
  defaut: number;
}

function SeuilsFraicheurSection() {
  const dispatch = useDispatch();
  const [lignes, setLignes] = useState<LigneSeuil[]>([]);
  const [saisies, setSaisies] = useState<Record<number, string>>({});
  const [bornes, setBornes] = useState({ min: 1, max: 1095 });
  const [ecriture, setEcriture] = useState(false);
  const [chargement, setChargement] = useState(true);

  const charger = async () => {
    setChargement(true);
    try {
      const [categories, etat] = await Promise.all([getTypeLieuCategories(), getSeuilsFraicheur()]);
      const regles = new Map(etat.seuils.map((s) => [s.categorieId, s.jours]));
      setBornes({ min: etat.joursMin, max: etat.joursMax });
      const l = categories.map((c) => ({
        categorieId: c.id,
        libelle: c.libelle,
        regle: regles.get(c.id) ?? null,
        defaut: etat.defautsParCategorie[c.libelle] ?? etat.defautJours,
      }));
      setLignes(l);
      setSaisies(Object.fromEntries(l.map((x) => [x.categorieId, String(x.regle ?? x.defaut)])));
    } catch (err: any) {
      dispatch(openSnackbar(messageSeuil(err, "Impossible de charger les seuils de fraîcheur."), "red"));
    }
    setChargement(false);
  };

  useEffect(() => {
    charger();
    getUser().then((res: any) => {
      if (res && res.role_type && res.role_type.freshness_write !== undefined) {
        setEcriture(!!res.role_type.freshness_write);
      }
    }).catch(() => dispatch(openSnackbar("Droits de l'administrateur illisibles : actions de modification masquées", "red")));
  }, []);

  const valide = (v: string) => /^\d+$/.test(v) && +v >= bornes.min && +v <= bornes.max;

  const enregistrer = async (l: LigneSeuil) => {
    const v = saisies[l.categorieId] ?? "";
    if (!valide(v)) {
      dispatch(openSnackbar(`Seuil entre ${bornes.min} et ${bornes.max} jours`, "red"));
      return;
    }
    dispatch(loaderOn());
    try {
      await reglerSeuilFraicheur(l.categorieId, l.libelle, +v);
      dispatch(openSnackbar(`${l.libelle} : ${v} jours`, "green"));
      await charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageSeuil(err, "Enregistrement impossible."), "red"));
    }
    dispatch(loaderOff());
  };

  const retablir = async (l: LigneSeuil) => {
    dispatch(loaderOn());
    try {
      await retirerSeuilFraicheur(l.categorieId);
      await charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageSeuil(err, "Retour au défaut impossible."), "red"));
    }
    dispatch(loaderOff());
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: C.textMuted, mb: 2 }}>
        Au-delà de ce nombre de jours sans vérification, un lieu collecté par un Spotter dans la catégorie compte comme
        « périmé » dans les trous de données. Sans réglage, la valeur par défaut s'applique.
      </Typography>
      <Box sx={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Catégorie</TableCell>
                <TableCell sx={headCellSx}>Seuil (jours)</TableCell>
                <TableCell sx={headCellSx}>Origine</TableCell>
                {ecriture && <TableCell sx={headCellSx}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {!chargement && lignes.length === 0 && (
                <TableRow sx={rowSx}>
                  <TableCell colSpan={4} sx={{ color: C.textMuted }}>Aucune catégorie au référentiel.</TableCell>
                </TableRow>
              )}
              {lignes.map((l) => {
                const saisie = saisies[l.categorieId] ?? "";
                return (
                  <TableRow key={l.categorieId} hover sx={rowSx}>
                    <TableCell>{l.libelle}</TableCell>
                    <TableCell>
                      {ecriture ? (
                        <TextField
                          size="small"
                          type="number"
                          value={saisie}
                          error={!valide(saisie)}
                          inputProps={{ min: bornes.min, max: bornes.max, "aria-label": `Seuil ${l.libelle}` }}
                          onChange={(e) => setSaisies((p) => ({ ...p, [l.categorieId]: e.target.value }))}
                          sx={{ ...inputSx, width: 110 }}
                        />
                      ) : (
                        l.regle ?? l.defaut
                      )}
                    </TableCell>
                    <TableCell sx={{ color: `${l.regle === null ? C.textMuted : C.text} !important` }}>
                      {l.regle === null ? "Défaut" : `Réglé (défaut ${l.defaut})`}
                    </TableCell>
                    {ecriture && (
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            onClick={() => enregistrer(l)}
                            disabled={!valide(saisie) || +saisie === (l.regle ?? -1)}
                            sx={accentBtn}
                          >
                            Enregistrer
                          </Button>
                          {l.regle !== null && (
                            <Button size="small" onClick={() => retablir(l)} sx={{ color: C.textSub, textTransform: "none" }}>
                              Revenir au défaut
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SECTION 3 — Champs additionnels POI
// ════════════════════════════════════════════════════════════════════════════
/* additional_master ne stocke que le nom, le type et le statut : « Requis »
   et l'ordre affichés ici n'étaient jamais enregistrés et ont été retirés
   (Z2-33). Les ajouter demanderait d'étendre l'entité et l'app. */
interface AddMoreRow {
  id: number;
  fieldName: string;
  fieldType: string;
}

const FIELD_TYPE_OPTIONS = [
  { value: "TEXT",         label: "Texte libre" },
  { value: "NUMBER",       label: "Nombre" },
  { value: "PHONE",        label: "Téléphone" },
  { value: "EMAIL",        label: "Email" },
  { value: "URL",          label: "Lien web" },
  { value: "DATE",         label: "Date" },
  { value: "BOOLEAN",      label: "Oui/Non" },
  { value: "SELECT",       label: "Choix unique" },
  { value: "MULTI_SELECT", label: "Choix multiples" },
  { value: "SCHEDULE",     label: "Horaires par jour" },
  { value: "PHOTO",        label: "Photo" },
  { value: "RATING",       label: "Note 1-5 étoiles" },
  { value: "hour",         label: "Heure" },
  { value: "websites",     label: "Site web (URL)" },
  { value: "string",       label: "Texte" },
  { value: "checkbox",     label: "Case à cocher" },
  { value: "text",         label: "text (ancien)" },
  { value: "number",       label: "number (ancien)" },
  { value: "date",         label: "date (ancien)" },
  { value: "select",       label: "select (ancien)" },
  { value: "photo",        label: "photo (ancien)" },
];

const emptyAddMoreForm = {
  id: { value: "", error: false, errorMessage: "Please enter addmore id" },
  fieldName: { value: "", error: false, errorMessage: "Nom de champ invalide" },
  fieldType: { value: "text", error: false, errorMessage: "Type de champ invalide" },
};

function AddMoreSection() {
  const dispatch = useDispatch();
  const [rows, setRows] = useState<AddMoreRow[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formValues, setFormValues] = useState<any>({ ...emptyAddMoreForm });
  const [addmoreId, setAddMoreId] = useState<any>(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [addmore_write, setAddmoreWrite] = useState(false);

  const resetForm = () => setFormValues({ ...emptyAddMoreForm });

  const validateForm = () => {
    let invalid = false;
    const updated = { ...formValues };
    const nameValid = formValues.fieldName.value.trim().length > 0;
    updated.fieldName = { ...formValues.fieldName, error: !nameValid };
    if (!nameValid) invalid = true;
    setFormValues(updated);
    return invalid;
  };

  const fetchAddMore = async () => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      const response = await getActiveAddMore();
      const list = Array.isArray(response) ? response : (response?.content ?? response?.data ?? []);
      setRows(list.map((r: any) => ({ id: r.id, fieldName: r.fieldName, fieldType: r.fieldType })));
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Impossible de charger les champs additionnels."), "red"));
    }
    dispatch(loaderOff());
  };

  useEffect(() => {
    fetchAddMore();
    getUser().then((res: any) => {
      if (res && res.role_type && res.role_type.addmore_write !== undefined) {
        setAddmoreWrite(res.role_type.addmore_write);
      }
    }).catch(() => dispatch(openSnackbar("Droits de l'administrateur illisibles : actions de modification masquées", "red")));
  }, []);

  const handleAdd = async () => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) { dispatch(openSnackbar("Session introuvable. Veuillez vous reconnecter.", "red")); return; }
    if (validateForm()) return;
    dispatch(loaderOn());
    try {
      await createAddmore({
        fieldName: formValues.fieldName.value,
        fieldType: formValues.fieldType.value,
      });
      resetForm(); setOpenAdd(false); fetchAddMore();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Ajout impossible."), "red"));
    }
    dispatch(loaderOff());
  };

  const handleEdit = async (id: any) => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) { dispatch(openSnackbar("Session introuvable. Veuillez vous reconnecter.", "red")); return; }
    if (validateForm()) return;
    dispatch(loaderOn());
    try {
      await updateAddmore(id, {
        fieldName: formValues.fieldName.value,
        fieldType: formValues.fieldType.value,
      });
      resetForm(); setOpenEdit(false); fetchAddMore();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Modification impossible."), "red"));
    }
    dispatch(loaderOff());
  };

  const handleDelete = async (id: any) => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) { dispatch(openSnackbar("Session introuvable. Veuillez vous reconnecter.", "red")); return; }
    dispatch(loaderOn());
    try {
      await deleteAddMore(id, { active_status: "Yes" });
      fetchAddMore();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Suppression impossible."), "red"));
    }
    dispatch(loaderOff());
  };

  const handleAutoFill = async (id: any) => {
    dispatch(loaderOn());
    try {
      const res = await getAddmorebyid(id);
      if (res) {
        setFormValues((prev: any) => ({
          ...prev,
          fieldName: { ...prev.fieldName, value: res.fieldName, error: false },
          fieldType: { ...prev.fieldType, value: res.fieldType || "text", error: false },
        }));
        setAddMoreId(id);
        setOpenEdit(true);
      }
    } catch { }
    dispatch(loaderOff());
  };

  const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        {addmore_write && (
          <Button onClick={() => { resetForm(); setOpenAdd(true); }} startIcon={<AddIcon />} sx={accentBtn}>
            Ajouter un champ
          </Button>
        )}
      </Box>

      <Box sx={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>N°</TableCell>
                <TableCell sx={headCellSx}>Nom du champ</TableCell>
                <TableCell sx={headCellSx}>Type</TableCell>
                {addmore_write && <TableCell sx={headCellSx}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((row, index) => {
                const globalIndex = page * rowsPerPage + index;
                return (
                  <TableRow key={row.id} hover sx={rowSx}>
                    <TableCell>{globalIndex + 1}</TableCell>
                    <TableCell>{row.fieldName}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          background: C.accentMuted,
                          color: C.accentLight,
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        {row.fieldType}
                      </Box>
                    </TableCell>
                    {addmore_write && (
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Box component="span" sx={{ cursor: "pointer" }} onClick={() => handleAutoFill(row.id)}>
                            <CreateIcon sx={{ color: "#5151ED", fontSize: 20 }} />
                          </Box>
                          <Box
                            component="span"
                            sx={{ cursor: "pointer" }}
                            onClick={() => { setSelectedId(row.id); setOpenDialog(true); }}
                          >
                            <DeleteIcon sx={{ color: C.error, fontSize: 20 }} />
                          </Box>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
          sx={{ color: C.textSub, borderTop: `1px solid ${C.border}` }}
        />
      </Box>

      <CommonDialog
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
        handleConfirm={() => { handleDelete(selectedId); setOpenDialog(false); }}
        contentText="Confirmer la suppression de ce champ ?"
      />

      {/* Add modal */}
      <Modal open={openAdd} onClose={() => setOpenAdd(false)}>
        <Box sx={modalBoxSx}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.text }}>
              Ajouter un champ
            </Typography>
            <CloseIcon onClick={() => setOpenAdd(false)} sx={{ color: C.textSub, cursor: "pointer" }} />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.textSub, mb: 0.5 }}>
                Nom du champ
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Ex: superficie"
                value={formValues.fieldName.value}
                error={formValues.fieldName.error}
                helperText={formValues.fieldName.error ? formValues.fieldName.errorMessage : ""}
                onChange={(e) =>
                  setFormValues((prev: any) => ({
                    ...prev,
                    fieldName: { ...prev.fieldName, value: e.target.value, error: e.target.value.trim().length === 0 },
                  }))
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.textSub, mb: 0.5 }}>
                Type de champ
              </Typography>
              <Select
                fullWidth
                size="small"
                value={formValues.fieldType.value}
                onChange={(e) =>
                  setFormValues((prev: any) => ({
                    ...prev,
                    fieldType: { ...prev.fieldType, value: e.target.value, error: false },
                  }))
                }
                sx={{
                  background: "rgba(31,30,27,0.04)",
                  color: C.text,
                  borderRadius: 1.5,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: C.border },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: C.borderAccent },
                  "& .MuiSvgIcon-root": { color: C.textMuted },
                }}
              >
                {FIELD_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button onClick={handleAdd} sx={accentBtn}>
              Ajouter
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Edit modal */}
      <Modal open={openEdit} onClose={() => { setOpenEdit(false); resetForm(); }}>
        <Box sx={modalBoxSx}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.text }}>
              Modifier le champ
            </Typography>
            <CloseIcon
              onClick={() => { setOpenEdit(false); resetForm(); }}
              sx={{ color: C.textSub, cursor: "pointer" }}
            />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.textSub, mb: 0.5 }}>
                Nom du champ
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formValues.fieldName.value}
                error={formValues.fieldName.error}
                helperText={formValues.fieldName.error ? formValues.fieldName.errorMessage : ""}
                onChange={(e) =>
                  setFormValues((prev: any) => ({
                    ...prev,
                    fieldName: { ...prev.fieldName, value: e.target.value, error: e.target.value.trim().length === 0 },
                  }))
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.textSub, mb: 0.5 }}>
                Type de champ
              </Typography>
              <Select
                fullWidth
                size="small"
                value={formValues.fieldType.value}
                onChange={(e) =>
                  setFormValues((prev: any) => ({
                    ...prev,
                    fieldType: { ...prev.fieldType, value: e.target.value, error: false },
                  }))
                }
                sx={{
                  background: "rgba(31,30,27,0.04)",
                  color: C.text,
                  borderRadius: 1.5,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: C.border },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: C.borderAccent },
                  "& .MuiSvgIcon-root": { color: C.textMuted },
                }}
              >
                {FIELD_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button onClick={() => handleEdit(addmoreId)} sx={accentBtn}>
              Mettre à jour
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

// Question 24 : les réglages anti-fraude (plafond Wave, délai entre
// notifications, seuil de blocage) n'étaient stockés que dans le navigateur,
// sans aucun effet : retirés. Le plafond de retrait relève de D2.

// ════════════════════════════════════════════════════════════════════════════
function VisionSection() {
  const dispatch = useDispatch();
  const [v, setV] = useState<any>({ visionActif: false, plafondMensuelVisionUsd: 50, seuilExifMinutes: 10, seuilExifMetres: 100, delaiExpirationJours: 30 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    getConfigActive().then((c: any) => {
      if (c) setV({
        visionActif: !!c.visionActif,
        plafondMensuelVisionUsd: c.plafondMensuelVisionUsd ?? 50,
        seuilExifMinutes: c.seuilExifMinutes ?? 10,
        seuilExifMetres: c.seuilExifMetres ?? 100,
        delaiExpirationJours: c.delaiExpirationJours ?? 30,
      });
      setLoading(false);
    });
  }, []);
  const sauver = async () => {
    setSaving(true);
    const r = await majVisionConfig(v);
    setSaving(false);
    dispatch(openSnackbar(r?.erreur ? "Échec de l'enregistrement" : "Réglages Vision enregistrés", r?.erreur ? "red" : "green"));
  };
  if (loading) return <div style={{ padding: 12 }}>Chargement…</div>;
  const champ = (label: string, key: string) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "8px 0" }}>
      <span>{label}</span>
      <input type="number" value={v[key]} onChange={(e) => setV({ ...v, [key]: Number(e.target.value) })}
        style={{ width: 120, padding: 6, borderRadius: 6 }} />
    </div>
  );
  return (
    <div style={{ padding: 12 }}>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
        Analyse photo automatique — aide à la décision, aucun effet sur le paiement. Le contrôle EXIF reste toujours actif.
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0", fontWeight: 700 }}>
        <input type="checkbox" checked={v.visionActif} onChange={(e) => setV({ ...v, visionActif: e.target.checked })} />
        Analyse Vision automatique activée
      </label>
      {champ("Plafond de dépense mensuel (USD)", "plafondMensuelVisionUsd")}
      {champ("Seuil écart date EXIF (minutes)", "seuilExifMinutes")}
      {champ("Seuil écart GPS EXIF (mètres)", "seuilExifMetres")}
      {champ("Délai d'expiration en attente (jours)", "delaiExpirationJours")}
      <Button onClick={sauver} disabled={saving}
        sx={{ mt: 1, background: "#1F1E1B", color: "#fff", textTransform: "none", "&:hover": { background: "#1F1E1B" } }}>
        {saving ? "Enregistrement…" : "Enregistrer"}
      </Button>
    </div>
  );
}

// ── Vague 2 : réglages d'intégrité de la capture ──────────────────────────────
const MODES = ["bloquant", "signal"];
function IntegriteSection() {
  const dispatch = useDispatch();
  const [v, setV] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [chgs, setChgs] = useState<any[]>([]);
  const [syn7, setSyn7] = useState<any>(null);
  const [syn30, setSyn30] = useState<any>(null);
  const cles = [
    "integriteActive", "integriteSeuilPrecisionMetres", "integriteModePrecision",
    "integriteSeuilDistanceMarqueurMetres", "integriteModeDistanceMarqueur", "integriteModePositionAbsente",
    "integriteModePositionSimulee", "integriteAttestationActive", "integriteModeAttestation",
    "integriteVersionMinimale", "integriteModeVersionMinimale", "integriteSeuilVitesseKmh", "integriteModeVitesse",
    "integriteModeSimPays", "integriteModeMultiCompte", "integritePaysAttendu",
    "questActivesStrictSansPosition", "questActivesMax",
  ];
  useEffect(() => {
    getConfigActive().then((c: any) => {
      const o: any = {};
      cles.forEach((k) => { o[k] = c?.[k]; });
      setV(o);
    });
    getIntegriteChangements().then((l: any) => Array.isArray(l) && setChgs(l));
    getSyntheseIntegrite(7).then(setSyn7);
    getSyntheseIntegrite(30).then(setSyn30);
    // eslint-disable-next-line
  }, []);
  const sauver = async () => {
    setSaving(true);
    const r = await majIntegriteConfig(v);
    setSaving(false);
    dispatch(openSnackbar(r?.erreur ? "Échec de l'enregistrement" : "Réglages d'intégrité enregistrés", r?.erreur ? "red" : "green"));
    getIntegriteChangements().then((l: any) => Array.isArray(l) && setChgs(l));
  };
  if (!v) return <div style={{ padding: 12 }}>Chargement…</div>;
  const ligne = (label: string, node: React.ReactNode) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "7px 0", gap: 12 }}>
      <span>{label}</span>{node}
    </div>
  );
  const bool = (k: string) => <input type="checkbox" checked={!!v[k]} onChange={(e) => setV({ ...v, [k]: e.target.checked })} />;
  const num = (k: string) => <input type="number" value={v[k] ?? ""} onChange={(e) => setV({ ...v, [k]: Number(e.target.value) })} style={{ width: 110, padding: 6, borderRadius: 6 }} />;
  const txt = (k: string) => <input type="text" value={v[k] ?? ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} style={{ width: 110, padding: 6, borderRadius: 6 }} />;
  const mode = (k: string) => (
    <select value={v[k] ?? "signal"} onChange={(e) => setV({ ...v, [k]: e.target.value })} style={{ padding: 6, borderRadius: 6 }}>
      {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
    </select>
  );
  return (
    <div style={{ padding: 12 }}>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
        Chaque règle a un seuil et un mode <b>bloquant</b> (refus) ou <b>signal</b> (revue admin). Tout est désactivé/toléré par défaut jusqu'à publication du build.
      </div>
      {ligne("Intégrité activée", bool("integriteActive"))}
      {ligne("Précision GPS max (m)", num("integriteSeuilPrecisionMetres"))}
      {ligne("— mode précision", mode("integriteModePrecision"))}
      {ligne("Distance marqueur↔appareil max (m)", num("integriteSeuilDistanceMarqueurMetres"))}
      {ligne("— mode distance marqueur", mode("integriteModeDistanceMarqueur"))}
      {ligne("— mode position absente", mode("integriteModePositionAbsente"))}
      {ligne("— mode position simulée", mode("integriteModePositionSimulee"))}
      {ligne("Attestation d'appareil activée", bool("integriteAttestationActive"))}
      {ligne("— mode attestation", mode("integriteModeAttestation"))}
      {ligne("Version minimale (numéro de build)", num("integriteVersionMinimale"))}
      {ligne("— mode version minimale", mode("integriteModeVersionMinimale"))}
      {ligne("Vitesse max entre captures (km/h)", num("integriteSeuilVitesseKmh"))}
      {ligne("— mode vitesse", mode("integriteModeVitesse"))}
      {ligne("— mode pays SIM", mode("integriteModeSimPays"))}
      {ligne("— mode multi-comptes", mode("integriteModeMultiCompte"))}
      {ligne("Pays attendu (ISO2)", txt("integritePaysAttendu"))}
      {ligne("Liste quêtes stricte sans position", bool("questActivesStrictSansPosition"))}
      {ligne("Quêtes max renvoyées", num("questActivesMax"))}
      <Button onClick={sauver} disabled={saving}
        sx={{ mt: 1, background: "#1F1E1B", color: "#fff", textTransform: "none", "&:hover": { background: "#1F1E1B" } }}>
        {saving ? "Enregistrement…" : "Enregistrer"}
      </Button>
      {(syn7 || syn30) && (() => {
        const r7 = syn7?.signauxParRegle ?? {}, r30 = syn30?.signauxParRegle ?? {};
        const regles = Array.from(new Set([...Object.keys(r7), ...Object.keys(r30)])).sort();
        return (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>Synthèse — rejets & signaux</div>
            <table style={{ fontSize: 11, borderCollapse: "collapse" }}>
              <thead><tr><th style={{ textAlign: "left", padding: "2px 12px 2px 0" }}>Règle</th><th>7 j</th><th style={{ paddingLeft: 12 }}>30 j</th></tr></thead>
              <tbody>
                <tr><td style={{ padding: "2px 12px 2px 0", fontWeight: 600 }}>Rejets (bloquants)</td>
                  <td align="center">{syn7?.rejetsIntegrite ?? 0}</td><td align="center">{syn30?.rejetsIntegrite ?? 0}</td></tr>
                {regles.map((k) => (
                  <tr key={k}><td style={{ padding: "2px 12px 2px 0" }}>{k}</td>
                    <td align="center">{r7[k] ?? 0}</td><td align="center">{r30[k] ?? 0}</td></tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4 }}>
              Captures analysées : {syn7?.nbSoumissions ?? 0} soum. + {syn7?.nbPois ?? 0} lieux (7 j).
            </div>
          </div>
        );
      })()}
      {chgs.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>Journal des changements</div>
          {chgs.slice(0, 15).map((c: any, i: number) => (
            <div key={i} style={{ fontSize: 11, opacity: 0.8 }}>
              {c.champ} : {c.ancienneValeur ?? c.ancienne} → {c.nouvelleValeur ?? c.nouvelle} ({c.auteur})
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Vague 2, lot 4 : comptes testeurs ─────────────────────────────────────────
// Un testeur voit les quêtes de la ville choisie (position virtuelle) ; ses
// captures sont acceptées malgré l'écart de position, marquées TEST, jamais
// payées ni comptées. Sert aux tests internes et au compte de revue Apple.
const VILLES_TESTEUR = ["dakar", "abidjan", "paris"];
function TesteursSection() {
  const dispatch = useDispatch();
  const [liste, setListe] = useState<any[] | null>(null);
  const [email, setEmail] = useState("");
  const [ville, setVille] = useState("dakar");
  const [aSupprimer, setASupprimer] = useState<string | null>(null);
  const erreur = (e: any, defaut: string) =>
    dispatch(openSnackbar(messageErreur(e, defaut), "red"));
  const charger = () => getTesteurs().then(setListe).catch((e) => { setListe([]); erreur(e, "Comptes testeurs illisibles"); });
  useEffect(() => { charger(); /* eslint-disable-next-line */ }, []);
  const enregistrer = async (em: string, vi: string, actif: boolean) => {
    try {
      await enregistrerTesteur(em.trim().toLowerCase(), vi, actif);
      dispatch(openSnackbar(actif ? "Compte testeur enregistré" : "Compte testeur désactivé", "green"));
      setEmail("");
      charger();
    } catch (e) { erreur(e, "Enregistrement impossible"); }
  };
  const supprimer = async () => {
    if (!aSupprimer) return;
    try { await supprimerTesteur(aSupprimer); dispatch(openSnackbar("Compte testeur retiré", "green")); charger(); }
    catch (e) { erreur(e, "Suppression impossible"); }
    setASupprimer(null);
  };
  if (!liste) return <div style={{ padding: 12 }}>Chargement…</div>;
  return (
    <div style={{ padding: 12 }}>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
        Un compte testeur voit les quêtes de la ville choisie, quelle que soit sa position réelle. Ses captures sont
        marquées <b>TEST</b> : jamais payées, jamais livrées au client, exclues des mesures. L'app affiche un bandeau « Mode testeur ».
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <input type="email" placeholder="email du compte Spotter" value={email} onChange={(e) => setEmail(e.target.value)}
          style={{ padding: 6, borderRadius: 6, minWidth: 240 }} />
        <select value={ville} onChange={(e) => setVille(e.target.value)} style={{ padding: 6, borderRadius: 6 }}>
          {VILLES_TESTEUR.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <Button disabled={!email.includes("@")} onClick={() => enregistrer(email, ville, true)}
          sx={{ background: "#1F1E1B", color: "#fff", textTransform: "none", "&:hover": { background: "#1F1E1B" } }}>
          Ajouter
        </Button>
      </div>
      {liste.length === 0 ? <div style={{ fontSize: 12, opacity: 0.7 }}>Aucun compte testeur.</div> : (
        <table style={{ fontSize: 12, borderCollapse: "collapse" }}>
          <thead><tr><th style={{ textAlign: "left", paddingRight: 16 }}>Email</th><th style={{ paddingRight: 16 }}>Ville</th><th style={{ paddingRight: 16 }}>Actif</th><th /></tr></thead>
          <tbody>
            {liste.map((t: any) => (
              <tr key={t.email}>
                <td style={{ paddingRight: 16 }}>{t.email}</td>
                <td style={{ paddingRight: 16 }}>
                  <select value={t.ville ?? "dakar"} onChange={(e) => enregistrer(t.email, e.target.value, t.actif)} style={{ padding: 4, borderRadius: 6 }}>
                    {VILLES_TESTEUR.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </td>
                <td style={{ paddingRight: 16 }} align="center">
                  <input type="checkbox" checked={!!t.actif} onChange={(e) => enregistrer(t.email, t.ville ?? "dakar", e.target.checked)} />
                </td>
                <td><Button size="small" onClick={() => setASupprimer(t.email)} sx={{ textTransform: "none", color: "#A23A0D" }}>Retirer</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <CommonDialog open={!!aSupprimer} handleClose={() => setASupprimer(null)} handleConfirm={supprimer}
        contentText={`Retirer ${aSupprimer ?? ""} des comptes testeurs ? Il redeviendra un Spotter ordinaire.`} />
    </div>
  );
}

/* La page regroupait toutes les sections ; la refonte les répartit :
   valeur du Tip et facturation dans Argent, le reste dans Réglages. Chaque
   route passe les sections à afficher ; le contenu des sections ne change pas. */
export default function Configuration({ sections: visibles, titre = "Contrôles et paramètres", phrase, domaine = "reglages", aide = AIDE_REGLAGES.parametres }: {
  sections?: string[]; titre?: string; phrase?: string; domaine?: Domaine; aide?: string;
} = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
  const isTokenExpired = useSelector((state: any) => state.auth.isTokenExpired);
  const { hash } = useLocation();

  /* /freshness et /add_more renvoient ici avec une ancre : on amène la
     section correspondante à l'écran. */
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [hash]);


  const sections = [
    {
      id: "tips",
      title: "Valeur du Tip et retraits",
      subtitle: "Valeur du Tip, minimum, plafond mensuel, second avis — super admin, motif obligatoire",
      content: <TipsSection />,
    },
    {
      id: "facturation",
      title: "Facturation",
      subtitle: "Entités émettrices par pays, mentions légales, archive des devis, contrats et factures",
      content: <FacturationSection />,
    },
    {
      id: "freshness",
      title: "Fraîcheur des données",
      subtitle: "Seuil par catégorie de lieu, utilisé par la détection des trous de données",
      content: <SeuilsFraicheurSection />,
    },
    {
      id: "addmore",
      title: "Champs additionnels des lieux",
      subtitle: "Questions supplémentaires du formulaire de lieu de l'application",
      content: <AddMoreSection />,
    },
    {
      id: "vision",
      title: "Analyse photo",
      subtitle: "Interrupteur, plafond mensuel, seuils EXIF — aide à la décision",
      content: <VisionSection />,
    },
    {
      id: "integrite",
      title: "Intégrité de la capture",
      subtitle: "Seuils, modes bloquant/signal, version minimale, attestation, journal, synthèse des rejets",
      content: <IntegriteSection />,
    },
    {
      id: "testeurs",
      title: "Comptes testeurs",
      subtitle: "Position virtuelle par ville, captures marquées TEST (tests internes, revue Apple)",
      content: <TesteursSection />,
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 }, minHeight: "100vh", background: C.bg }}>
      <EnTetePage
        domaine={domaine}
        titre={titre}
        phrase={phrase ?? "Paramètres de la plateforme Tribe. Chaque modification est tracée."}
        aide={aide}
      />

      {sections.filter(section => !visibles || visibles.includes(section.id)).map((section) => (
        <Accordion key={section.id} id={section.id} defaultExpanded sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: C.accentLight }} />}
            sx={accordionSummarySx}
          >
            <Box>
              <Typography sx={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 1.25 }}>
                <TroisBarres taille="s" />
                {section.title}
              </Typography>
              <Typography sx={{ fontSize: 12, color: C.textMuted, mt: 0.25 }}>
                {section.subtitle}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1, pb: 3, px: 3 }}>
            {section.content}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
